import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Briefcase, Search } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useBrand } from "@/context/BrandContext";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable } from "@/components/common/DataTable";
import { EmptyState } from "@/components/common/EmptyState";
import { StatusChip, statusTone } from "@/components/common/StatusChip";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { CompanyDialog } from "@/features/companies/CompanyDialog";
import { MasterDataReviewDialog } from "@/features/companies/MasterDataReview";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export default function CompaniesPage() {
  const { t } = useT();
  const { brandParam } = useBrand();
  const { can } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();

  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const [tipo, setTipo] = useState("all");
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState("nombre");
  const [order, setOrder] = useState(1);
  const [dialogOpen, setDialogOpen] = useState(params.get("new") === "1");
  const [reviewOpen, setReviewOpen] = useState(false);   // Fix U
  const [editing, setEditing] = useState(null);
  const [confirm, setConfirm] = useState(null);

  useEffect(() => {
    const id = setTimeout(() => {
      setDebounced(q);
      setPage(1);
    }, 250);
    return () => clearTimeout(id);
  }, [q]);

  useEffect(() => {
    if (params.get("new") === "1") {
      setDialogOpen(true);
      params.delete("new");
      setParams(params, { replace: true });
    }
  }, [params, setParams]);

  const pageSize = 25;
  const { data, isLoading } = useQuery({
    queryKey: ["companies", { debounced, tipo, status, page, sort, order, brandParam }],
    queryFn: async () =>
      (
        await api.get("/companies", {
          params: {
            q: debounced || undefined,
            tipo: tipo === "all" ? undefined : tipo,
            status: status === "all" ? undefined : status,
            brand: brandParam,
            page,
            page_size: pageSize,
            sort,
            order,
          },
        })
      ).data,
  });

  const statusMutation = useMutation({
    mutationFn: async ({ id, next }) =>
      (await api.patch(`/companies/${id}/status`, null, { params: { status: next } })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["companies"] });
      toast.success(t("app.saved"));
    },
    onError: (e) => toast.error(apiError(e, t("app.error"))),
  });

  const columns = [
    {
      key: "nombre",
      label: t("companies.name"),
      sortable: true,
      render: (r) => <span className="font-medium">{r.nombre}</span>,
    },
    {
      key: "brand",
      label: t("app.brand"),
      width: 100,
      render: (r) => <StatusChip tone="blue" label={r.brand === "bb" ? "B&B" : "Betos"} />,
    },
    { key: "tipo", label: t("companies.type"), width: 130, render: (r) => t(`companies.types.${r.tipo}`) },
    { key: "edificios_count", label: t("companies.buildings"), width: 100, align: "right" },
    { key: "ordenes_abiertas", label: t("companies.open_orders"), width: 130, align: "right" },
    {
      key: "status",
      label: t("app.status"),
      width: 110,
      render: (r) => <StatusChip tone={statusTone(r.status)} label={t(`status.${r.status}`)} />,
    },
    {
      key: "acciones",
      label: "",
      width: 60,
      align: "right",
      render: (r) =>
        can("companies", "update") && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                data-testid={`company-actions-${r.id}`}
                onClick={(e) => e.stopPropagation()}
                className="h-7 rounded-[6px] px-2 text-[12px]"
              >
                •••
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="rounded-[8px]">
              <DropdownMenuItem
                data-testid={`company-edit-${r.id}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setEditing(r);
                  setDialogOpen(true);
                }}
              >
                {t("app.edit")}
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid={`company-toggle-${r.id}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setConfirm({ id: r.id, next: r.status === "active" ? "inactive" : "active" });
                }}
              >
                {r.status === "active" ? t("app.deactivate") : t("app.activate")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ),
    },
  ];

  return (
    <>
      <PageHeader
        title={t("companies.title")}
        subtitle={t("companies.subtitle")}
        testId="companies-header"
        actions={
          <>
            {/* Fix U (2026-10-05): compañías repetidas, casas cargadas como edificio, etc. */}
            {can("companies", "update") && (
              <Button variant="outline" data-testid="master-data-review-button" onClick={() => setReviewOpen(true)}
                className="h-8 rounded-[6px] text-[13px]">
                {t("master_data.button")}
              </Button>
            )}
            {can("companies", "create") && (
              <Button
                data-testid="new-company-button"
                onClick={() => {
                  setEditing(null);
                  setDialogOpen(true);
                }}
                className="h-8 rounded-[6px] bg-brand text-[13px] hover:bg-brand-hover"
              >
                <Plus className="mr-1.5 h-3.5 w-3.5" />
                {t("companies.new")}
              </Button>
            )}
          </>
        }
      />

      <div className="flex flex-wrap items-center gap-2 border-b border-line bg-white px-6 py-2.5">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-tertiary" />
          <Input
            data-testid="companies-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("companies.search")}
            className="h-8 w-[280px] rounded-[6px] pl-8 text-[13px]"
          />
        </div>
        <Select value={tipo} onValueChange={(v) => { setTipo(v); setPage(1); }}>
          <SelectTrigger data-testid="companies-type-filter" className="h-8 w-[150px] rounded-[6px] text-[13px]">
            <SelectValue placeholder={t("companies.type")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("app.all")}</SelectItem>
            <SelectItem value="multifamily">{t("companies.types.multifamily")}</SelectItem>
            <SelectItem value="residencial">{t("companies.types.residencial")}</SelectItem>
            <SelectItem value="comercial">{t("companies.types.comercial")}</SelectItem>
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={(v) => { setStatus(v); setPage(1); }}>
          <SelectTrigger data-testid="companies-status-filter" className="h-8 w-[130px] rounded-[6px] text-[13px]">
            <SelectValue placeholder={t("app.status")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("app.all")}</SelectItem>
            <SelectItem value="active">{t("status.active")}</SelectItem>
            <SelectItem value="inactive">{t("status.inactive")}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="flex min-h-0 flex-1 flex-col bg-white" data-testid="companies-page">
        <DataTable
          testId="companies-table"
          columns={columns}
          rows={data?.items}
          total={data?.total || 0}
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          loading={isLoading}
          sort={sort}
          order={order}
          onSortChange={(key) => {
            setSort(key);
            setOrder((o) => (sort === key ? -o : 1));
          }}
          onRowClick={(r) => navigate(`/companias/${r.id}`)}
          empty={
            <EmptyState
              icon={Briefcase}
              testId="companies-empty"
              title={t("companies.empty_title")}
              description={t("companies.empty_desc")}
              action={
                can("companies", "create") && (
                  <Button
                    data-testid="companies-empty-new"
                    onClick={() => setDialogOpen(true)}
                    className="h-8 rounded-[6px] bg-brand text-[13px] hover:bg-brand-hover"
                  >
                    {t("companies.new")}
                  </Button>
                )
              }
            />
          }
        />
      </div>

      <MasterDataReviewDialog open={reviewOpen} onOpenChange={setReviewOpen} />
      <CompanyDialog
        open={dialogOpen}
        onOpenChange={(v) => {
          setDialogOpen(v);
          if (!v) setEditing(null);
        }}
        company={editing}
      />
      <ConfirmDialog
        open={!!confirm}
        onOpenChange={(v) => !v && setConfirm(null)}
        title={confirm?.next === "inactive" ? t("confirm.deactivate_title") : t("confirm.activate_title")}
        description={confirm?.next === "inactive" ? t("confirm.deactivate_desc") : t("confirm.activate_desc")}
        onConfirm={() => {
          statusMutation.mutate(confirm);
          setConfirm(null);
        }}
      />
    </>
  );
}
